import { ScrollDispatcher } from '@angular/cdk/overlay';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { _isDev, _log, _warn } from '@shared/aux_helper_environment';
import {
  _cloneDeep,
  _debounceDecorator,
  _equal,
  _getNewNumberId,
  _orderBy,
  _timeout,
  _uniqueElements,
  _uniqueElementsByKey,
} from '@shared/aux_helper_functions';
import { IanTranslateService } from 'core/services/ian-core-singleton.service';
import { Subject } from 'rxjs';
import { delay, first, filter } from 'rxjs/operators';
import { GenericTagsSelectV2Service } from './generic-tag-select-v2.service';
import { GenericTagsSelectMultiplev2AttributeModal } from './generic-tags-select-multiple-v2-attribute.modal';
import {
  TagTreeSelectedModel,
  TagTreeSelectedModelInternal,
  TagTreeWithRootSelectedModal,
  _aux_filterTagsRootExistByRootId,
  _aux_getTagItemObjFromId,
  _aux_getTagRootFromId,
  _aux_tagTreeGroupFormat,
} from './generic-tags-select-v2.component';

//sirve para cuando se usa @output onChangeGroup y ingresas los datos seleccionados @input selectedTags
export const _mapTagIdTagValuesToIds = (tags: TagTreeWithRootSelectedModal[]): TagTreeSelectedModel[] => {
  return (tags || [])
    .map(t => t.tagValues)
    .flat()
    .map(id => {
      return { id: id };
    });
};

@Component({
  selector: 'generic-tags-select-multiple-v2',
  template: `
    <ng-container>
      <ng-container *ngIf="groupTags == null && showLoader">
        <div class="spinner-container-mini">
          <custom-loader></custom-loader>
        </div>
      </ng-container>

      <div
        *ngIf="groupTags != null"
        [class]="deafaultParentClass"
        [ngClass]="{
          'generic-tags-select-multiple-root': !useAsAdvancedFilter,
          'generic-tags-select-multiple-advanced-filter-root': useAsAdvancedFilter
        }"
      >
        <p *ngIf="!!title" class="form-filter title-filter">
          {{ title }}
          <span [matTooltip]="'COMP.GEN_TAGS.STORES.FILTER_TAG_VALUES' | pTranslate">
            <button mat-icon-button *ngIf="filterTagsValuesSwitch" (click)="filterTagsValues(!tagValuesByResultsOnList)">
              <mat-icon [ngClass]="{ 'status-black': !tagValuesByResultsOnList, 'status-green': tagValuesByResultsOnList }">
                filter_list
              </mat-icon>
            </button>
          </span>
        </p>

        <p *ngIf="usingAutoVisibleFallback && autoVisibleTagsRootsFallbackHintEnabled" class="auto-visible-fallback-hint">
          <mat-icon class="auto-visible-fallback-hint-icon">info_outline</mat-icon>
          {{ 'COMP.GEN_TAGS.AUTO_VISIBLE_FALLBACK_HINT' | pTranslate }}
        </p>

        <!-- PRICING-S02: celdas opcionales (ej. FreeNumber) proyectadas en la misma grilla -->
        <ng-content select="[tag-grid-cell]"></ng-content>

        <div *ngFor="let tag of groupTags; let i = index; trackBy: trackByRootTagId" class="generic-tags-select-multiple-celd">
          <ng-container *ngIf="false">
            {{ tag | json }}
          </ng-container>
          <generic-tags-select-v2
            *ngIf="tag?.rootTagId != null"
            [tagsModuleType]="tagsModuleType"
            [rootTagId]="tag.rootTagId"
            [required]="visibleTagsRootsRequired?.length && visibleTagsRootsRequired.includes(tag.rootTagId)"
            [levels]="configLevelsSize"
            [level1Config]="configLevel1 || configLevels"
            [level2Config]="configLevel2 || configLevels"
            [level3Config]="configLevel3 || configLevels"
            [level4Config]="configLevel4 || configLevels"
            [configLevels]="configLevels"
            [onFilterTagsValues]="tagValuesByResultsOnList"
            [verbose]="verbose"
            [autoFillParentsOnStart]="autoFillParentsOnStart"
            [autoFillParentsOnAlways]="autoFillParentsOnAlways"
            [selected]="tag.selected || []"
            [placeholder]="i == 0 ? placeholder : null"
            [prefixPlaceholder]="_getPrefixPlaceholder(tag.rootTagId)"
            [postfixPlaceholder]="postfixPlaceholder"
            [showClearAllBtn]="showClearAllBtn"
            (onChange)="changeSelected($event, i)"
            [autoSelectRequiredTagByDefaultTagValue]="autoSelectRequiredTagByDefaultTagValue"
            (openedChange)="onOpenedChange($event)"
          >
          </generic-tags-select-v2>
        </div>
      </div>

      <div fxLayout="row" class="mt-20 mb-20" *ngIf="groupTags != null && (addMoreItemsButton || useAsAdvancedFilter)">
        <div fxFlex fxLayoutAlign="end start">
          <button
            mat-icon-button
            color="accent"
            matTooltip="{{ 'ADMIN.MASTER.STORES.ADD_ATRIBUTE_BTN' | pTranslate }}"
            (click)="openNewtagDialog()"
            data-test-id="add_tags"
            [disabled]="configLevels?.disabled == true || noMoreTagsToSelect()"
          >
            <span class="material-icons font-size-30">add_circle_outline</span>
          </button>
        </div>
      </div>
    </ng-container>
  `,
  styles: [
    `
      .generic-tags-select-multiple-root {
        display: flex;
        flex-wrap: wrap;
        row-gap: 24px;
      }

      .generic-tags-select-multiple-advanced-filter-root {
        display: flex;
        flex-wrap: wrap;
      }

      .generic-tags-select-multiple-celd {
        flex: 1 1 calc(100%);
        max-width: calc(100%);
      }

      .auto-visible-fallback-hint {
        display: flex;
        align-items: center;
        gap: 4px;
        margin: 0 0 12px 0;
        font-size: 12px;
        font-style: italic;
        opacity: 0.7;
      }

      .auto-visible-fallback-hint-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
        line-height: 16px;
      }

      @media (min-width: 960px) {
        .generic-tags-select-multiple-root {
          column-gap: 32px;
        }

        .generic-tags-select-multiple-root.flex-33,
        .generic-tags-select-multiple-root.flex-50 {
          row-gap: 32px;
        }

        ::ng-deep .generic-tags-select-multiple-root.flex-33 .generic-lookup,
        ::ng-deep .generic-tags-select-multiple-root.flex-50 .generic-lookup {
          max-height: 56px;
        }

        .generic-tags-select-multiple-root.flex-33 > .generic-tags-select-multiple-celd {
          flex: 1 1 calc(33% - 32px);
          max-width: calc(33% - 32px);
        }
        .generic-tags-select-multiple-root.flex-33 > .generic-tags-select-multiple-celd:nth-child(3n) {
          flex: 1 1 33%;
          max-width: 33%;
        }

        .generic-tags-select-multiple-root.flex-50 > .generic-tags-select-multiple-celd {
          flex: 1 1 calc(50% - 32px);
          max-width: calc(50% - 32px);
        }
        .generic-tags-select-multiple-root.flex-50 > .generic-tags-select-multiple-celd:nth-child(2n) {
          flex: 1 1 50%;
          max-width: 50%;
        }
      }
    `,
  ],
})
export class GenericTagsSelectMultipleV2Component implements OnInit, OnDestroy {
  $unsuscribreAll: Subject<void> = new Subject<void>();
  destroyed = false;
  tagTree = null;
  allFirstLevelTags = [];
  groupTags: TagTreeSelectedModelInternal[] = null;

  @Input('tagsModuleType') tagsModuleType = 1;
  @Input('placeholder') placeholder: string = null;
  @Input('prefixPlaceholder') prefixPlaceholder = '';
  @Input('postfixPlaceholder') postfixPlaceholder = '';
  @Input('orderPrefix') orderPrefixPlaceholder = null;
  @Input('showLoader') showLoader = true;

  @Input('configLevels') configLevels: any = { multiple: true, parentDependent: false };
  @Input('configLevel1') configLevel1 = null;
  @Input('configLevel2') configLevel2 = null;
  @Input('configLevel3') configLevel3 = null;
  @Input('configLevel4') configLevel4 = null;

  @Input('configLevelsSize') configLevelsSize = [1, 2, 3];
  @Input('visibleTagsRoots') visibleTagsRoots: number[] | { id: number; isRequired?: boolean }[] = null;
  @Input('visibleTagsValues') visibleTagsValues: string[] = null;
  @Input('visibleTagsValuesIds') visibleTagsValuesIds: number[] = null;
  @Input('filterTagsValuesSwitch') filterTagsValuesSwitch = false;
  @Input('tagValuesByResultsOnList') tagValuesByResultsOnList = false;
  @Input('selectedTags') selectedTags: TagTreeSelectedModel[] = null;
  @Input('title') title: string = null;
  @Input('verbose') verbose = false;
  @Input('autoFillParentsOnStart') autoFillParentsOnStart = false;
  @Input('autoFillParentsOnAlways') autoFillParentsOnAlways = false;
  @Input('deafaultParentClass') deafaultParentClass: 'flex-33' | 'flex-50' | 'flex-100' = 'flex-33';
  @Input('addMoreItemsButton') addMoreItemsButton = true;
  @Input('useAsAdvancedFilter') useAsAdvancedFilter = false;
  @Input('showClearAllBtn') showClearAllBtn = true;
  @Input('autoVisibleTagsRoots') autoVisibleTagsRoots = false; //obtiene los tags requeridos/obligatorios para mostrar por pantalla del primer nivel. Se activa cuando no se quiere enviar visibleTagsRoots
  /**
   * Fallback cuando autoVisibleTagsRoots=true pero ningún tag del módulo vino marcado isRequired desde el maestro
   * (antes: no se mostraba ningún filtro de etiqueta, aunque hubiera etiquetas cargadas). Muestra las primeras N
   * raíces en vez de nada. El orden es el que devuelve el endpoint attributes-by-tag-target (geTags() solo ordena
   * alfabéticamente los VALUES de cada tag, no las raíces entre sí — no asumir orden alfabético acá).
   * Kill switch: poner en `false` en el @Input del componente padre para volver al comportamiento previo
   * (nada visible por defecto) sin tocar este archivo, por si el tope trae problemas en algún módulo con muchas raíces.
   */
  @Input('autoVisibleTagsRootsFallbackEnabled') autoVisibleTagsRootsFallbackEnabled = true;
  @Input('autoVisibleTagsRootsFallbackLimit') autoVisibleTagsRootsFallbackLimit = 5;
  /** Kill switch propio del aviso ("No hay etiquetas por defecto...") — el fallback en sí sigue activo aunque esto sea false. */
  @Input('autoVisibleTagsRootsFallbackHintEnabled') autoVisibleTagsRootsFallbackHintEnabled = true;
  /** true cuando visibleTagsRoots se llenó por el fallback (sin isRequired) y no por selección explícita del maestro. */
  usingAutoVisibleFallback = false;
  @Input('autoSelectRequiredTagByDefaultTagValue') autoSelectRequiredTagByDefaultTagValue = false;
  @Input('tmpFix_PROM_397_markForCheck') tmpFix_PROM_397_markForCheck = false; // PROM-397
  /** PRICING-S04: rootIds que no se pueden agregar con "+" (p.ej. tags de composición en Datos Principales). */
  @Input('blockedRootIds') blockedRootIds: number[] = null;

  @Output() onChange: EventEmitter<TagTreeSelectedModel[]> = new EventEmitter();
  @Output() onChangeGroup: EventEmitter<TagTreeWithRootSelectedModal[]> = new EventEmitter();
  @Output() onChangeList: EventEmitter<boolean> = new EventEmitter();
  @Output() isLoaded: EventEmitter<boolean> = new EventEmitter();
  @Output() onChangeFilterTagValues: EventEmitter<boolean> = new EventEmitter();
  @Output() initialValues: EventEmitter<number[]> = new EventEmitter();
  /** PRICING-S02: ids agregados con "+" (p.ej. FreeNumber) para que el padre los muestre como input libre. */
  @Output() userAddedRootIdsChange: EventEmitter<number[]> = new EventEmitter();

  @Output() openedChange: EventEmitter<boolean> = new EventEmitter(); // Emite cuando se abre o cierra el select

  userVisibleTagsRoots: number[] = null;
  visibleTagsRootsRequired = [];
  visibleTagsIds = [];

  trackByRootTagId = rTag => rTag.rootTagId;

  /** Detecta FreeNumber desde nodos del árbol (incluye placeholders vacíos). */
  private isFreeNumberRootId(rootId: number): boolean {
    if (rootId == null || !this.tagTree?.[0]?.values?.length) return false;
    const hit = this.tagTree[0].values.find(v => Number(v?.tagId) === Number(rootId));
    const dt = hit?.dataType as any;
    return dt === 5 || String(dt).toLowerCase() === 'freenumber';
  }

  /**/
  constructor(
    protected cd: ChangeDetectorRef,
    readonly sd: ScrollDispatcher,
    public translate: IanTranslateService,
    protected service: GenericTagsSelectV2Service,
    protected dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.service
      .geTagsTree(this.tagsModuleType)
      .pipe(first())
      .subscribe(async data => {
        this.tagTree = data || [];

        this.initialValues.emit(this.tagTree?.flatMap(tag => tag?.values?.map(value => value?.id) || []));

        const _visibleTagsRoots = [];

        this.allFirstLevelTags =
          this.tagTree?.length && this.tagTree[0]?.values?.length
            ? this.tagTree[0].values.reduce((acc, element) => {
                if (element?.parentId === 0 && !acc.includes(element.tagId)) {
                  acc.push(element.tagId);
                  if (element.isRequired && (!this.visibleTagsRoots || this.visibleTagsRoots?.length > 0) && this.autoVisibleTagsRoots) {
                    _visibleTagsRoots.push(element.tagId);
                  }
                }
                return acc;
              }, [])
            : [];
        if ((!this.visibleTagsRoots || this.visibleTagsRoots?.length > 0) && this.autoVisibleTagsRoots) {
          const useFallback = _visibleTagsRoots.length === 0 && this.autoVisibleTagsRootsFallbackEnabled;
          this.usingAutoVisibleFallback = useFallback && this.allFirstLevelTags.length > 0;
          this.visibleTagsRoots = useFallback ? this.allFirstLevelTags.slice(0, this.autoVisibleTagsRootsFallbackLimit) : _visibleTagsRoots;
        }
        this.creatGroupTagsFromImputs();
        if (
          ((this.filterTagsValuesSwitch && this.tagValuesByResultsOnList) || !this.filterTagsValuesSwitch) &&
          !!this.visibleTagsValues &&
          this.visibleTagsValues?.length > 0
        )
          this.setTagValuesIdsByTagValueNames();
        if (
          ((this.filterTagsValuesSwitch && this.tagValuesByResultsOnList) || !this.filterTagsValuesSwitch) &&
          !!this.visibleTagsValuesIds &&
          this.visibleTagsValuesIds?.length > 0
        ) {
          this.setIntersectGeneralConfigLevel();
        }
        this.isLoaded.emit(true);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (false && changes.selectedTags) _log('[GenericTagsSelectMultipleV2Component]', this.selectedTags);

    const changeVisibleTagsValues =
      ((this.filterTagsValuesSwitch && this.tagValuesByResultsOnList) || !this.filterTagsValuesSwitch) &&
      !!this.tagTree &&
      !!this.visibleTagsValues &&
      changes.visibleTagsValues &&
      !_equal(changes.visibleTagsValues.currentValue, changes.visibleTagsValues.previousValue);

    if (changeVisibleTagsValues) {
      this.setTagValuesIdsByTagValueNames();
    }

    const changeVisibleTagsValuesIds =
      ((this.filterTagsValuesSwitch && this.tagValuesByResultsOnList) || !this.filterTagsValuesSwitch) &&
      this.tagTree &&
      !!this.visibleTagsValuesIds &&
      changes.visibleTagsValuesIds &&
      !_equal(changes.visibleTagsValuesIds.currentValue, changes.visibleTagsValuesIds.previousValue);

    if (changeVisibleTagsValuesIds) {
      this.setIntersectGeneralConfigLevel();
    }

    const changeSelectedTags =
      this.tagTree && changes.selectedTags && !_equal(changes.selectedTags.currentValue, changes.selectedTags.previousValue);

    if (changeSelectedTags) {
      this.creatGroupTagsFromImputs();
      return;
    }

    //

    const changeVisibleTagsRoots =
      this.tagTree && changes.visibleTagsRoots && !_equal(changes.visibleTagsRoots.currentValue, changes.visibleTagsRoots.previousValue);

    if (changeVisibleTagsRoots) {
      this.creatGroupTagsFromImputs();
      return;
    }
  }

  setTagValuesIdsByTagValueNames() {
    const flatTagTreeValues = this.tagTree.flatMap(tag => tag.values);

    this.visibleTagsValuesIds = flatTagTreeValues
      .filter(tagValue => this.visibleTagsValues.includes(tagValue.value))
      .map(tagValue => {
        this.visibleTagsIds = [...new Set([...this.visibleTagsIds, tagValue.tagId])];
        return tagValue.id;
      });

    this.setIntersectGeneralConfigLevel();
  }

  setIntersectGeneralConfigLevel() {
    if (!_equal(this.configLevels?.intersect, this.visibleTagsValuesIds)) {
      const selectedValues = this.selectedTags.flatMap(tag => tag.id);
      this.visibleTagsValuesIds = [...new Set([...this.visibleTagsValuesIds, ...selectedValues])];
      this.configLevels = { ...this.configLevels, intersect: this.visibleTagsValuesIds };
    }
  }

  getFlatvisibleTagsRoots() {
    let visibleTagsRoots =
      this.visibleTagsRoots?.length && (this.visibleTagsRoots as any)[0]?.id
        ? (this.visibleTagsRoots as any).map(v => v.id)
        : this.visibleTagsRoots;

    return visibleTagsRoots || [];
  }

  private getBlockedRootIdSet(): Set<number> {
    return new Set((this.blockedRootIds || []).map(id => Number(id)).filter(id => id > 0));
  }

  /** Roots ya visibles + bloqueados (no deben ofrecerse en el modal "+"). */
  private getAlreadyAddedRootIds(): number[] {
    const blocked = [...this.getBlockedRootIdSet()];
    return [...new Set([...(this.getFlatvisibleTagsRoots() || []), ...blocked])];
  }

  noMoreTagsToSelect(): boolean {
    if (!this.userVisibleTagsRoots?.length) return false;
    return this.visibleTagsIds?.length > 0
      ? this.groupTags?.length >= this.visibleTagsIds?.length
      : this.groupTags?.length >= this.allFirstLevelTags?.length;
  }

  //WARNINH: Este método demora considerablemente si el set de datos es grande (sobre todo _aux_filterTagsRootExistByRootId) / DES-2214
  creatGroupTagsFromImputs() {
    if (this.tagTree == null) return;

    if (false) _log(_aux_getTagRootFromId(13, this.tagTree));

    this.makeRequieredTags();

    const requiredIds: number[] = [...(this.visibleTagsRootsRequired || [])];
    const blocked = this.getBlockedRootIdSet();
    const userAddedIds: number[] = [...(this.userVisibleTagsRoots || [])].filter(
      id => id != null && !requiredIds.includes(id) && !blocked.has(Number(id))
    );
    const visibleTagsRoots = this.getFlatvisibleTagsRoots();

    // selected por root
    const selectedByRoot = new Map<number, TagTreeSelectedModel[]>();
    (this.selectedTags || []).forEach(tag => {
      if (tag?.id == null) return;
      // 1) árbol local  2) árbol global (valores creados inline PRICING-S03)  3) tagId del payload
      let rootId = _aux_getTagRootFromId(tag.id, this.tagTree);
      if (!rootId) rootId = _aux_getTagRootFromId(tag.id);
      if (!rootId && (tag as any)?.tagId != null) rootId = Number((tag as any).tagId);
      if (!rootId) return;
      if (blocked.has(Number(rootId))) return;

      // Sync del valor nuevo al árbol local para que el lookup lo resuelva después.
      this.ensureTagValueInLocalTree(tag.id, rootId, (tag as any)?.value);

      const list = selectedByRoot.get(rootId) || [];
      list.push({ id: tag.id, value: (tag as any).value, tagId: rootId } as any);
      selectedByRoot.set(rootId, _uniqueElementsByKey(list as any, 'id') as any);
    });

    // Roots "ya presentes" (visibles o con selección), excluyendo required y los recién agregados con +
    const existingOtherIds = _uniqueElements([
      ...visibleTagsRoots,
      ...Array.from(selectedByRoot.keys()),
      ...((this.groupTags || []).map(g => g.rootTagId) as number[]),
    ]).filter(id => id != null && !requiredIds.includes(id) && !userAddedIds.includes(id));

    // Preservar orden previo de los "others" para no saltar al guardar
    const prevOrder = (this.groupTags || []).map(g => g.rootTagId);
    existingOtherIds.sort((a, b) => {
      const ia = prevOrder.indexOf(a);
      const ib = prevOrder.indexOf(b);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });

    // Orden final: obligatorias → resto existente → agregadas con + (siempre al final)
    // FreeNumber no va al cascade (se renderiza como input libre en el padre).
    // blockedRootIds (S04 compose) tampoco.
    const orderedRootIds = this.applyDisplayOrder(
      _uniqueElements([...requiredIds, ...existingOtherIds, ...userAddedIds]).filter(
        id => !this.isFreeNumberRootId(id) && !blocked.has(Number(id))
      ),
      prevOrder
    );

    let rv = orderedRootIds.map(rootTagId => ({
      rootTagId,
      selected: selectedByRoot.get(rootTagId) || [],
    }));

    const rvFiltered = _aux_filterTagsRootExistByRootId(rv, this.tagTree);

    this.userAddedRootIdsChange.emit([...(this.userVisibleTagsRoots || [])]);

    if (
      // groupTags == null es el primer render: hay que asignar aunque rvFiltered sea [], si no el spinner
      // (*ngIf="groupTags == null && showLoader") queda trabado para siempre cuando no hay ningún tag para mostrar
      // (ej. autoVisibleTagsRootsFallbackEnabled=false y ningún tag isRequired, o un módulo sin tags configurados).
      this.groupTags != null &&
      _equal(_orderBy(rvFiltered, ['rootTagId']), _orderBy(this.groupTags || [], ['rootTagId'])) &&
      _equal(
        rvFiltered.map(g => (g.selected || []).map(s => s.id)),
        (this.groupTags || []).map(g => (g.selected || []).map(s => s.id))
      )
    ) {
      // Misma composición; si el orden de root cambió, igual actualizamos.
      const sameOrder =
        (this.groupTags || []).length === rvFiltered.length &&
        (this.groupTags || []).every((g, i) => g.rootTagId === rvFiltered[i]?.rootTagId);
      if (sameOrder) return;
    }

    this.groupTags = rvFiltered;
    if (false && _isDev()) _log('[creatGroupTagsFromImputs]', this.groupTags?.length);

    this.onChangeList.emit(true);
  }

  /**
   * Orden de display. Las etiquetas de tienda (tagsModuleType 2) se muestran al revés del orden que arma
   * `creatGroupTagsFromImputs` (commit "reverse store tags").
   *
   * NO invertir `this.groupTags` al final del recálculo: el recálculo siguiente reconstruye el orden a partir
   * de `prevOrder`, que ya viene invertido, así que un segundo `reverse()` vuelve a dar vuelta la lista. Y hay
   * un recálculo por CADA selección del usuario (changeSelected → onChange → el padre reescribe selectedTags →
   * ngOnChanges), con lo cual la lista alternaba de orden en cada click y el overlay del lookup —anclado a la
   * posición en el DOM— terminaba mostrando los valores de otra etiqueta.
   *
   * Por eso la inversión se aplica solo a las raíces que todavía NO están en pantalla: el primer armado se
   * invierte entero (igual que antes) y los recálculos posteriores respetan lo que el usuario ya está viendo.
   * Efecto lateral buscado: en stores, las etiquetas agregadas con "+" ahora aparecen al final, como en el
   * resto de los módulos, en vez de saltar al principio.
   */
  private applyDisplayOrder(rootIds: number[], prevOrder: number[]): number[] {
    if (this.tagsModuleType !== 2) return rootIds;

    const onScreen = rootIds.filter(id => prevOrder.includes(id)).sort((a, b) => prevOrder.indexOf(a) - prevOrder.indexOf(b));
    const notOnScreenYet = rootIds.filter(id => !prevOrder.includes(id)).reverse();

    return [...onScreen, ...notOnScreenYet];
  }

  makeRequieredTags() {
    let rv = [];

    (this.visibleTagsRoots || []).forEach(obj => {
      if (obj?.isRequired) rv.push(obj.id);
    });

    if (!_equal(rv, this.visibleTagsRootsRequired)) {
      this.visibleTagsRootsRequired = rv;
    }
  }

  changeSelected(ev: TagTreeSelectedModel[], key) {
    if (this.groupTags && this.groupTags[key]) {
      if (this.tmpFix_PROM_397_markForCheck) {
        //Defer the update to the next JavaScript macrotask (event loop tick)
        Promise.resolve().then(() => {
          this.groupTags[key].selected = ev;
          this.convertAndemitSelecteds();
          this.cd.markForCheck(); // Notify Angular that changes were made
        });
      } else {
        this.groupTags[key].selected = ev;
        this.convertAndemitSelecteds();
      }
    }
  }

  /**
   * PRICING-S03: un valor creado inline vive en `_aux_treeTags` pero no en `this.tagTree`.
   * Sin este sync, al round-trip de selectedTags se pierde la selección en el select.
   */
  private ensureTagValueInLocalTree(valueId: number, rootTagId: number, value?: string) {
    if (valueId == null || !this.tagTree?.[0]) return;

    const existsLocal = _aux_getTagItemObjFromId([valueId], this.tagTree);
    if (existsLocal?.length) return;

    const fromGlobal = _aux_getTagItemObjFromId([valueId]);
    const node =
      fromGlobal?.[0] ||
      ({
        id: valueId,
        value: value || '',
        name: value || '',
        level: 1,
        parentId: 0,
        tagId: rootTagId,
        values: [],
      } as any);

    if (!this.tagTree[0].values) this.tagTree[0].values = [];
    if (!this.tagTree[0].values.some(v => Number(v?.id) === Number(valueId))) {
      this.tagTree[0].values = [...this.tagTree[0].values, { ...node }];
    }
  }

  convertAndemitSelecteds() {
    let rv_plainIds: TagTreeSelectedModel[] = [];

    //Convert TagTreeSelectedModelInternal[] 2 TagTreeSelectedModel[]
    (this.groupTags || []).forEach(tag => {
      (tag?.selected || []).forEach($sel => {
        if ($sel?.id == null) return;

        let sel = $sel;
        if (sel.value == null) {
          sel = _cloneDeep($sel);
          let val = _aux_getTagItemObjFromId([sel.id], this.tagTree);
          if (!(val && val[0])) val = _aux_getTagItemObjFromId([sel.id]);
          if (val && val[0]) sel.value = val[0].value;
        }

        const withRoot = {
          ...sel,
          tagId: (sel as any).tagId != null ? Number((sel as any).tagId) : tag.rootTagId,
        };
        rv_plainIds = [...rv_plainIds, withRoot];
      });
    });

    rv_plainIds = _uniqueElementsByKey(rv_plainIds as any, 'id');

    if (_equal(_orderBy(rv_plainIds, ['id']), _orderBy(this.selectedTags, ['id']))) return;

    this.emitChanges(rv_plainIds);
  }

  @_debounceDecorator(0)
  emitChanges(rv_plainIds) {
    if (true && this.verbose) _log('\n\n[emitSelected Multiple Tags]', { groupTags: this.groupTags, plainIds: rv_plainIds });
    this.onChange.emit(rv_plainIds);
    const rv_group = _aux_tagTreeGroupFormat(this.groupTags);
    this.onChangeGroup.emit(rv_group);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.$unsuscribreAll.next();
    this.$unsuscribreAll.complete();
  }

  ngAfterViewInit(): void {}

  openNewtagDialog() {
    // PRICING-S03: invalidar caché para traer etiquetas/flags recién creados en el maestro.
    this.service.invalidateModuleCache(this.tagsModuleType);
    this.dialog
      .open(GenericTagsSelectMultiplev2AttributeModal, {
        width: '550px',
        data: {
          tagsModuleType: this.tagsModuleType,
          alreadyAdded: this.getAlreadyAddedRootIds(),
          excludeList: this.visibleTagsIds || [],
          selected: this.userVisibleTagsRoots,
        },
      })
      .afterClosed()
      .pipe(first())
      .subscribe(data => {
        if (true) _log('AttributeModal data:', data);
        if (data !== null) {
          const blocked = this.getBlockedRootIdSet();
          this.userVisibleTagsRoots = (data || []).filter(id => !blocked.has(Number(id)));
          // Refrescar árbol (post-invalidate) para que el CTA inline vea allowDirectValueLoad.
          this.service
            .geTagsTree(this.tagsModuleType)
            .pipe(first())
            .subscribe(tree => {
              this.tagTree = tree || [];
              this.creatGroupTagsFromImputs();
            });
        }
      });
  }

  filterTagsValues(toogle: boolean) {
    if (toogle) {
      if (this.visibleTagsValues?.length > 0) {
        this.setTagValuesIdsByTagValueNames();
      }
      if (!this.visibleTagsValues && this.visibleTagsValuesIds?.length > 0) {
        this.setIntersectGeneralConfigLevel();
      }
    } else {
      const { intersect, ...rest } = this.configLevels;
      this.configLevels = { ...rest };
    }
    this.tagValuesByResultsOnList = toogle;
    this.onChangeFilterTagValues.emit(toogle);
  }

  onOpenedChange(event) {
    this.openedChange.emit(event);
  }

  _getPrefixPlaceholder(tagId: number) {
    if (this.prefixPlaceholder?.length > 0 || !(this.orderPrefixPlaceholder?.length > 0)) return this.prefixPlaceholder;
    const order = this.orderPrefixPlaceholder.find(o => o.tagId === tagId)?.order;
    return order ? order + '-' : '';
  }
}
